import React, { useEffect, useState } from "react";
import { Box, Stack, Typography, Button, Divider } from "@mui/material";
import {
  OutlinedSelectButton,
  CustomOptions,
} from "../HelperFunctions/HelperFunctions";

const viewMetrics = [
  { key: "IsRevenue", label: "Revenue" },
  { key: "IsLabour", label: "Labor" },
  { key: "IsCOGSAndExp", label: "COGS & Expenses" },
  { key: "IsOtherExpense", label: "Other Controllable Metrics" },
];
const comparisons = [
  { key: "LY", label: "LY" },
  { key: "LyAndVariance", label: "LY $ Var" },
  { key: "LyAndVariancePerctange", label: "LY % Var" },
];

const negativeFigureOptions = [
  "Normal",
  "Parentheses",
  "With a trailing Minus",
];

const panelSx = {
  border: "1px solid #d0d0d0",
  borderRadius: 1,
  p: 1.5,
  display: "flex",
  alignItems: "center",
  gap: 2,
  flex: 1,
  minHeight: 72,
};

const labelSx = {
  fontSize: 11,
  color: "#555",
  width: 140,
  flexShrink: 0,
};

const ViewAndDisplayViewer = ({
  onChange,
  initialValues,
  selectedCorporationCount,
}) => {
 
  const [tab, setTab] = useState("view");
  const [metrics, setMetrics] = useState({
    IsRevenue: true,
    IsLabour: true,
    IsCOGSAndExp: true,
    IsOtherExpense: true,
    viewType: 2,
  });
  const [comparison, setComparison] = useState({
    LY: true,
    LyAndVariance: true,
    LyAndVariancePerctange: true,
  });
  const [display, setDisplay] = useState({
    IsNegativeInRed: false,
    IsWithoutCents: false,
    ShowZeroValue: false,
    IsNoSaleTrans: false,
    IsParentheses: false,
    IsTrailingMinus: false,
  });
  const reportByOptions = [
    { label: "Daily", value: 1 },
    { label: "Weekly", value: 2 },
    { label: "Monthly", value: 3 },
    { label: "Quarterly", value: 4 },
  ];

  useEffect(() => {
    onChange?.({ metrics, comparison, display });
  }, [metrics, comparison, display]);

  useEffect(() => {
    if (initialValues) {
      setMetrics((prev) => ({ ...prev, ...initialValues }));
      setComparison((prev) => ({ ...prev, ...initialValues }));
      setDisplay((prev) => ({ ...prev, ...initialValues }));
    }
  }, [initialValues]);

  const reportBy = reportByOptions.find(
    (item) => item.value === metrics.viewType,
  )?.label;

  const toggle = (setter, key) =>
    setter((prev) => ({ ...prev, [key]: !prev[key] }));

  return (
    <Box p={2}>
      <Stack
        direction="row"
        mb={2}
        sx={{
          border: "1px solid #4384b4",
          borderRadius: 1,
          overflow: "hidden",
          width: "fit-content",
        }}
      >
        {["view", "display"].map((item) => (
          <Button
            key={item}
            onClick={() => setTab(item)}
            sx={{
              textTransform: "none",
              borderRadius: 0,
              px: 4,
              bgcolor: tab === item ? "#4384b4" : "#fff",
              color: tab === item ? "#fff" : "#4384b4",
            }}
          >
            {item === "view" ? "View" : "Display"}
          </Button>
        ))}
      </Stack>

      {tab === "view" ? (
        <Stack spacing={2}>
          {selectedCorporationCount <= 1 && (
            <>
              <Stack direction="row" alignItems="center" spacing={5}>
                <Typography fontSize={14} color="#555">
                  Report By
                </Typography>
                {reportByOptions.map((opt) => (
                  <OutlinedSelectButton
                    key={opt.value}
                    label={opt.label}
                    selected={metrics.viewType === opt.value}
                    onClick={() =>
                      setMetrics((prev) => ({
                        ...prev,
                        viewType: opt.value,
                      }))
                    }
                    width="110px"
                  />
                ))}
              </Stack>

              <Typography fontSize={14} fontWeight={600}>
                {reportBy}
              </Typography>
            </>
          )}
          <Divider sx={{ borderColor: "#90caf9", borderBottomWidth: 2 }} />

          <Stack direction="row" spacing={2} alignItems="stretch">
            <Box flex={1}>
              <Stack spacing={3} p={2}>
                <Stack direction="row" spacing={1}>
                  {viewMetrics.slice(0, 2).map((m) => (
                    <Box key={m.key} flex={1}>
                      <CustomOptions
                        type={m.label}
                        selected={metrics[m.key]}
                        onClick={() => toggle(setMetrics, m.key)}
                      />
                    </Box>
                  ))}
                </Stack>

                <Stack direction="row" spacing={1}>
                  {viewMetrics.slice(2).map((m) => (
                    <Box key={m.key} flex={1}>
                      <CustomOptions
                        type={m.label}
                        selected={metrics[m.key]}
                        onClick={() => toggle(setMetrics, m.key)}
                      />
                    </Box>
                  ))}
                </Stack>
              </Stack>
            </Box>

            <Divider orientation="vertical" flexItem />

            <Box>
              <Typography fontSize={13} color="#555">
                Comparison on Revenue section
              </Typography>
              <Stack gap={2} p={2}>
                {comparisons.map((c) => (
                  <CustomOptions
                    key={c.key}
                    type={c.label}
                    selected={comparison[c.key]}
                    onClick={() => toggle(setComparison, c.key)}
                    width="140px"
                  />
                ))}
              </Stack>
            </Box>
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1.5} alignItems="stretch">
            <Box sx={{ ...panelSx, alignItems: "flex-start", p: 3 }}>
              <Typography sx={{ ...labelSx, p: 1 }}>
                Show Negative Figures
              </Typography>
              <Stack flex={1} p={1}>
                <Stack direction="row" py={0.5} gap={1}>
                  {negativeFigureOptions.map((opt) => (
                    <OutlinedSelectButton
                      key={opt}
                      label={opt}
                      selected={
                        opt === "Normal"
                          ? !display.IsParentheses && !display.IsTrailingMinus
                          : opt === "Parentheses"
                            ? display.IsParentheses
                            : display.IsTrailingMinus
                      }
                      onClick={() => {
                        setDisplay((prev) => ({
                          ...prev,
                          IsParentheses: opt === "Parentheses",
                          IsTrailingMinus: opt === "With a trailing Minus",
                        }));
                      }}
                      width={
                        opt === "With a trailing Minus" ? "180px" : "120px"
                      }
                    />
                  ))}
                </Stack>
                <CustomOptions
                  type="Show Negative figures in Red"
                  selected={display.IsNegativeInRed}
                  onClick={() => toggle(setDisplay, "IsNegativeInRed")}
                  width="100%"
                />
              </Stack>
            </Box>

            <Box sx={panelSx}>
              <Typography sx={labelSx}>Show Figures</Typography>
              <Stack flex={1}>
                <CustomOptions
                  type="Without cents"
                  selected={display.IsWithoutCents}
                  onClick={() => toggle(setDisplay, "IsWithoutCents")}
                  width="100%"
                />
                <CustomOptions
                  type="Exclude Zero Values"
                  selected={display.ShowZeroValue}
                  onClick={() => toggle(setDisplay, "ShowZeroValue")}
                  width="100%"
                />
              </Stack>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1.5} alignItems="stretch">
            <Box sx={panelSx}>
              <Typography sx={labelSx}>Show Sales</Typography>
              <Box flex={1}>
                <CustomOptions
                  type="Don't Show No sales Rows"
                  selected={display.IsNoSaleTrans}
                  onClick={() => toggle(setDisplay, "IsNoSaleTrans")}
                  width="100%"
                />
              </Box>
            </Box>

            {/* <Box sx={panelSx}>
              <Typography sx={labelSx}>View report</Typography>
              <Box flex={1}>
                <CustomOptions
                  type="Show report in full view"
                  selected={display.showFullView}
                  onClick={() => toggle(setDisplay, "showFullView")}
                  width="100%"
                />
              </Box>
            </Box> */}
          </Stack>
        </Stack>
      )}
    </Box>
  );
};

export default ViewAndDisplayViewer;
